import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice, nanoid } from '@reduxjs/toolkit';
import { langToCountryCodeMap } from 'assets/countryCodeConfig';
import {
  getCachedGroupAttributes,
  getFormattedThemeName,
  getIsSessionExpired,
  setPrevSessionDataToLocalStorage,
} from 'utils';
import { cacheSessionId, getSessionId } from 'utils/session';

import { LIGHT_THEME_MODE_TYPE } from '../../services/theme';
import { appChannelTypes, FlowTypes, SESSION_PROLONG_TIME, UIThemeTypes } from '../constants';
import { formatInitialQueryParams } from '../formatters';
import type { RootState } from '../index';
import type { PlatformsViewport } from '../models';
import { PayHubDomainSessionsEnumsPlatformType } from '../models';

export interface ParamsSnapshot {
  sessionId: string;
  preselectedPayGroupId: string | null;
  lang: string;
  brand: string;
  userId: string;
  clickstreamId: string;
  metrics: number;
  xChannel?: string;
  isApplePayAvailable?: boolean;
  isGPayAvailable?: boolean;
  parentWidth: number;
  parentHeight: number;
  selectedPayGroupId?: number;
  merchantRedirectUrl?: string;
  inPopUp?: boolean;
  isAlwaysClose?: boolean;
  isInitLoading?: boolean;
  mode: string;
  hidenav?: boolean;
}

export type FlowLockReason = 'init' | 'auto-select' | 'auto-submit' | null;

interface FlowLockState {
  isLocked: boolean;
  reason: FlowLockReason;
}

export type DisplaySelectListOptionsType = {
  [key: string]: boolean;
} | null;

export interface CommonState extends ParamsSnapshot {
  paymentFlow: FlowTypes;
  isSessionExpired: boolean;
  sessionExpirationTime: number;
  UITheme: UIThemeTypes;
  platform: PayHubDomainSessionsEnumsPlatformType;
  appChannel: appChannelTypes;
  isStatusPageMode?: boolean;
  isCustomIframeMode?: boolean;
  userPhone: string;
  countryCode: string;
  initialTheme: string;
  isSubmitted: boolean;
  needsInitRefetch: boolean;
  userBalance?: number;
  amount: string;
  growthbookFeatures?: string[];
  isGrowthbookInitialized: boolean;
  logoSizes: {
    [key: string]: number;
  };
  userBlocked: boolean;
  success_page_redirect_timeout: number;
  platformsViewport?: PlatformsViewport;
  merchantRedirectUrl?: string;
  merchantUrl?: string;
  merchantSuccessUrl?: string;
  merchantFailUrl?: string;
  merchantProcessingUrl?: string;
  merchantRedirectType?: string;
  notificationConnectionOpen: boolean;
  displaySelectListOptions: DisplaySelectListOptionsType;
  amountTouched: boolean;
  showPCIDSSLogo: boolean;
  flowLockState: FlowLockState;
  navigationKey: string;
  isStatusPageCleanupRequired: boolean;
}

export interface ReturnUrls {
  merchantUrl?: string;
  merchantSuccessUrl?: string;
  merchantFailUrl?: string;
  merchantProcessingUrl?: string;
  merchantRedirectType?: string;
}

export const initialState: CommonState = {
  paymentFlow: FlowTypes.deposit,
  UITheme: UIThemeTypes.modulor,
  appChannel: appChannelTypes.unknown,
  sessionId: '',
  lang: 'en',
  brand: '',
  clickstreamId: '',
  userId: '',
  userPhone: '',
  metrics: 0,
  parentWidth: window.innerWidth,
  parentHeight: window.innerHeight,
  preselectedPayGroupId: null,
  isSessionExpired: false,
  sessionExpirationTime: new Date().getTime() + SESSION_PROLONG_TIME.S,
  platform: PayHubDomainSessionsEnumsPlatformType.Desktop,
  isStatusPageMode: false,
  isCustomIframeMode: false,
  needsInitRefetch: false,
  countryCode: '',
  initialTheme: '',
  isSubmitted: false,
  userBalance: 0,
  amount: '',
  isGrowthbookInitialized: false,
  success_page_redirect_timeout: 30,
  logoSizes: {
    height: 40,
    width: 40,
  },
  notificationConnectionOpen: false,
  displaySelectListOptions: null,
  isInitLoading: true,
  amountTouched: false,
  mode: LIGHT_THEME_MODE_TYPE,
  showPCIDSSLogo: false,
  flowLockState: {
    isLocked: false,
    reason: null,
  },
  merchantRedirectUrl: undefined,
  userBlocked: false,
  navigationKey: 'initial',
  isStatusPageCleanupRequired: false,
};

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setWidgetFlow(state, action: PayloadAction<FlowTypes>) {
      return {
        ...state,
        paymentFlow: action.payload,
      };
    },
    setAppChannel(state, action: PayloadAction<appChannelTypes>) {
      return {
        ...state,
        appChannel: action.payload,
      };
    },
    setParamsSnapshot(state, action: PayloadAction<string>) {
      const params = formatInitialQueryParams(action.payload);
      if (params.sessionId) {
        cacheSessionId(params.sessionId);
      }
      return {
        ...state,
        ...params,
      };
    },
    setSessionExpired(state) {
      return {
        ...state,
        isSessionExpired: true,
      };
    },
    setPlatform(state, action: PayloadAction<PayHubDomainSessionsEnumsPlatformType>) {
      return {
        ...state,
        platform: action.payload,
      };
    },
    setUserPhone(state, action: PayloadAction<string>) {
      return {
        ...state,
        userPhone: action.payload,
      };
    },
    setLogoSizes(state, action: PayloadAction<{ [key: string]: number }>) {
      return {
        ...state,
        logoSizes: action.payload,
      };
    },
    setGrowthbookFeatures(state, action: PayloadAction<string[]>) {
      return {
        ...state,
        growthbookFeatures: action.payload,
      };
    },
    setGrowthbookInitialized(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isGrowthbookInitialized: action.payload,
      };
    },
    setCountryCode(state, action: PayloadAction<string>) {
      return {
        ...state,
        countryCode: action.payload,
      };
    },
    checkAndSetIsSessionExpired(state) {
      const expirationTime = new Date().getTime() + SESSION_PROLONG_TIME.L;
      const sessionIdFromUrl = getSessionId();
      const currentSessionId = sessionIdFromUrl || state.sessionId || '';
      const isSessionExpired = getIsSessionExpired({
        sessionIdSaved: currentSessionId,
        sessionExpirationTime: expirationTime,
      });

      if (currentSessionId) {
        cacheSessionId(currentSessionId);
        setPrevSessionDataToLocalStorage({
          sessionId: currentSessionId,
          sessionExpirationTime: expirationTime,
        });
      }

      return {
        ...state,
        isSessionExpired,
        sessionId: state.sessionId || currentSessionId,
        sessionExpirationTime: expirationTime,
      };
    },
    setSelectedPayGroupId(state, action: PayloadAction<number>) {
      return {
        ...state,
        selectedPayGroupId: action.payload,
      };
    },
    setIsStatusPageMode(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isStatusPageMode: action.payload,
      };
    },
    setIsCustomIframeMode(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCustomIframeMode: action.payload,
      };
    },
    setInitialTheme(state, action: PayloadAction<string>) {
      return {
        ...state,
        initialTheme: action.payload,
      };
    },
    setIsSubmitted(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isSubmitted: action.payload,
      };
    },
    setUserBalance(state, action: PayloadAction<number | undefined>) {
      return {
        ...state,
        userBalance: action.payload,
      };
    },
    setAmount(state, action: PayloadAction<string>) {
      return {
        ...state,
        amount: action.payload,
      };
    },
    setAmountTouched(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        amountTouched: action.payload,
      };
    },
    setSuccessPageRedirectTimeout: (state, action: PayloadAction<number>) => ({
      ...state,
      success_page_redirect_timeout: action.payload,
    }),
    setPlatformsViewport(state, action: PayloadAction<PlatformsViewport>) {
      state.platformsViewport = action.payload;
    },
    setReturnUrls(state, action: PayloadAction<ReturnUrls>) {
      const { merchantUrl, merchantSuccessUrl, merchantFailUrl, merchantProcessingUrl, merchantRedirectType } =
        action.payload;

      return {
        ...state,
        merchantUrl,
        merchantSuccessUrl,
        merchantFailUrl,
        merchantProcessingUrl,
        merchantRedirectType,
      };
    },
    resetReturnUrls(state) {
      return {
        ...state,
        merchantUrl: undefined,
        merchantSuccessUrl: undefined,
        merchantFailUrl: undefined,
        merchantProcessingUrl: undefined,
        merchantRedirectType: undefined,
      };
    },
    resetMerchantRedirectUrl(state) {
      return {
        ...state,
        merchantRedirectUrl: undefined,
      };
    },
    notificationConnectionOpenOn(state) {
      return {
        ...state,
        notificationConnectionOpen: true,
      };
    },
    notificationConnectionOpenOff(state) {
      return {
        ...state,
        notificationConnectionOpen: false,
      };
    },
    setDisplaySelectListOptions(state, action: PayloadAction<DisplaySelectListOptionsType>) {
      return {
        ...state,
        displaySelectListOptions: action.payload,
      };
    },
    setNeedsInitRefetch(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        needsInitRefetch: action.payload,
      };
    },
    setIsInitLoading(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isInitLoading: action.payload,
      };
    },
    setShowPCIDSSLogo(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        showPCIDSSLogo: action.payload,
      };
    },
    lockFlow: (state, action: PayloadAction<FlowLockReason>) => {
      state.flowLockState.isLocked = true;
      state.flowLockState.reason = action.payload;
    },
    unlockFlow: (state) => {
      state.flowLockState.isLocked = false;
      state.flowLockState.reason = null;
    },
    setUserBlocked: (state, action) => {
      state.userBlocked = action.payload;
    },
    resetNavigationKey: (state) => {
      state.navigationKey = nanoid();
    },
    setStatusPageCleanupRequired: (state, action) => {
      state.isStatusPageCleanupRequired = action.payload;
    },
  },
});

export const brandSelector = ({ common }: { common: CommonState }) => common.brand || 'COM';
export const themeUISelector = ({ common }: { common: CommonState }) => common.UITheme;
export const initialBrandSelector = ({ common }: { common: CommonState }) => common.brand;
export const langSelector = ({ common }: { common: CommonState }) => common.lang;
export const clickstreamIdSelector = ({ common }: { common: CommonState }) => common.clickstreamId;
export const appChannelSelector = ({ common }: { common: CommonState }) => common.appChannel;
export const isSessionExpiredSelector = ({ common }: { common: CommonState }) => common.isSessionExpired;

export const sessionExpirationTimeSelector = ({ common }: { common: CommonState }) => common.sessionExpirationTime;

export const selectedPayGroupIdSelector = createSelector(
  ({ common }: { common: CommonState }) => common.selectedPayGroupId,
  (payGroupId) => payGroupId || getCachedGroupAttributes().payGroupIdAttr,
);

export const sessionIdSelector = ({ common }: { common: CommonState }) => common.sessionId || '';
export const isApplePayAvailableSelector = ({ common }: { common: CommonState }) => common.isApplePayAvailable || false;
export const isGooglePayAvailableSelector = ({ common }: { common: CommonState }) => common.isGPayAvailable || false;

export const paymentFlowSelector = ({ common }: { common: CommonState }) => common.paymentFlow;

export const userIdSelector = ({ common }: { common: CommonState }) => common.userId;

export const platformSelector = ({ common }: { common: CommonState }) => common.platform;

export const isStatusPageModeSelector = ({ common }: { common: CommonState }) => common.isStatusPageMode || false;

export const isCustomIframeModeSelector = ({ common }: { common: CommonState }) => common.isCustomIframeMode || false;

export const userPhoneSelector = ({ common }: { common: CommonState }) => common.userPhone;

export const logoSizesSelector = ({ common }: { common: CommonState }) => common.logoSizes;

export const growthbookFeaturesSelector = (state: RootState) => state.common.growthbookFeatures;

export const isGrowthbookInitializedSelector = (state: RootState) => state.common.isGrowthbookInitialized;

export const countryCodeSelector = createSelector(
  ({ common }: { common: CommonState }) => common.countryCode,
  (countryCode) => langToCountryCodeMap[countryCode] || countryCode,
);

export const initialThemeSelector = createSelector(
  ({ common }: { common: CommonState }) => common.initialTheme,
  (theme) => getFormattedThemeName(theme),
);

export const isSubmittedSelector = ({ common }: { common: CommonState }) => common.isSubmitted;

export const userBalanceSelector = ({ common }: { common: CommonState }) => common.userBalance;

export const amountSelector = ({ common }: { common: CommonState }) => common.amount || '';

export const successPageRedirectTimeoutSelector = ({ common }: { common: CommonState }) =>
  common.success_page_redirect_timeout;

export const getPlatformsViewportSelector = ({ common }: { common: CommonState }) => common.platformsViewport;

const merchantUrlSelector = ({ common }: { common: CommonState }) => common.merchantUrl;

const merchantSuccessUrlSelector = ({ common }: { common: CommonState }) => common.merchantSuccessUrl;

const merchantFailUrlSelector = ({ common }: { common: CommonState }) => common.merchantFailUrl;

const merchantProcessingUrlSelector = ({ common }: { common: CommonState }) => common.merchantProcessingUrl;

const merchantRedirectTypeSelector = ({ common }: { common: CommonState }) => common.merchantRedirectType;

export const notificationConnectionOpenSelector = ({ common }: { common: CommonState }) =>
  common.notificationConnectionOpen;

export const displaySelectListOptionsSelector = ({ common }: { common: CommonState }) =>
  common.displaySelectListOptions;

export const needsInitRefetchSelector = ({ common }: { common: CommonState }) => common.needsInitRefetch || false;

export const isInPopUpSelector = ({ common }: { common: CommonState }) => Boolean(common.inPopUp);

export const isAlwaysCloseSelector = ({ common }: { common: CommonState }) => Boolean(common.isAlwaysClose);

export const isInitLoadingSelector = ({ common }: { common: CommonState }) => common.isInitLoading;

export const amountTouchedSelector = ({ common }: { common: CommonState }) => common.amountTouched;

export const merchantRedirectUrlSelector = ({ common }: { common: CommonState }) => common.merchantRedirectUrl;

export const returnUrlsSelector = createSelector(
  [
    merchantUrlSelector,
    merchantSuccessUrlSelector,
    merchantFailUrlSelector,
    merchantProcessingUrlSelector,
    merchantRedirectTypeSelector,
    merchantRedirectUrlSelector,
  ],
  (
    merchantUrl,
    merchantSuccessUrl,
    merchantFailUrl,
    merchantProcessingUrl,
    merchantRedirectType,
    merchantRedirectUrl,
  ) => ({
    merchantUrl,
    merchantSuccessUrl,
    merchantFailUrl,
    merchantProcessingUrl,
    merchantRedirectType,
    merchantRedirectUrl,
  }),
);

export const themeModeSelector = ({ common }: { common: CommonState }) => common.mode;

export const hideNavSelector = ({ common }: { common: CommonState }) => common.hidenav;

export const showPCIDSSLogoSelector = ({ common }: { common: CommonState }) => common.showPCIDSSLogo;

export const flowLockSelector = ({ common }: { common: CommonState }) => common.flowLockState;
export const userBlockedSelector = ({ common }: { common: CommonState }) => common.userBlocked;
export const navigationKeySelector = ({ common }: { common: CommonState }) => common.navigationKey;
export const isStatusPageCleanupRequiredSelector = ({ common }: { common: CommonState }) =>
  common.isStatusPageCleanupRequired;

export const {
  setWidgetFlow,
  setAppChannel,
  setParamsSnapshot,
  checkAndSetIsSessionExpired,
  setSessionExpired,
  setSelectedPayGroupId,
  setPlatform,
  setIsStatusPageMode,
  setIsCustomIframeMode,
  setUserPhone,
  setCountryCode,
  setLogoSizes,
  setNeedsInitRefetch,
  setGrowthbookFeatures,
  setGrowthbookInitialized,
  setInitialTheme,
  setIsSubmitted,
  setUserBalance,
  setAmount,
  setAmountTouched,
  setSuccessPageRedirectTimeout,
  setPlatformsViewport,
  setReturnUrls,
  resetReturnUrls,
  resetMerchantRedirectUrl,
  notificationConnectionOpenOn,
  notificationConnectionOpenOff,
  setDisplaySelectListOptions,
  setIsInitLoading,
  setShowPCIDSSLogo,
  resetNavigationKey,
  setStatusPageCleanupRequired,
  lockFlow,
  unlockFlow,
  setUserBlocked,
} = commonSlice.actions;

export default commonSlice.reducer;
